import Phaser from 'phaser';
import { ComponentType, ComponentCategory } from '@/components/ComponentDefs';
import { EventBus, GameEvents } from '@/events/EventBus';
import { COMPONENT_SPACING, EnemyWeaponType } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { createComponentVisual } from '@/systems/ComponentIcons';
import { SoundManager } from '@/systems/SoundManager';
import { popupFontSize } from '@/entities/Robot';
import type { InstalledComponent } from '@/entities/Robot';
import type { RobotHost } from './RobotHost';

/**
 * Handles all damage-related logic for a Robot: taking damage, ricochet checks,
 * component breakage, structural integrity checks, ejection, cracks, and popups.
 */
export class RobotDamageSystem {
  /** Accumulated damage for popup display (small per-frame hits get batched) */
  private pendingPopupDmg: number = 0;

  /** Crack thresholds: light cracks below 70%, heavy cracks below 40% */
  private static readonly CRACK_LIGHT = 0.7;
  private static readonly CRACK_HEAVY = 0.4;

  constructor(private host: RobotHost, private fx: import('./RobotFX').RobotFX) {}

  takeDamage(amount: number, attackerX?: number, attackerY?: number): void {
    if (!this.host.alive) return;
    if (GOD.infiniteHp) return;

    // Stab Legs damage reduction (anchored platform = tougher)
    const stabLegsDamageReduction = this.host.components.find(
      c => c.def.type === ComponentType.STAB_LEGS && !c.broken
    ) ? 0.2 : 0;
    if (stabLegsDamageReduction > 0) {
      amount *= (1 - stabLegsDamageReduction);
    }

    // Pick the component most exposed to the attack direction
    const exposed = this.host.components.filter(c => c.exposed && !c.broken && c.compMaxHp > 0);
    if (exposed.length > 0) {
      let target: InstalledComponent;

      if (attackerX !== undefined && attackerY !== undefined) {
        // Directional damage: hit the component closest to the attacker
        const adx = attackerX - this.host.x;
        const ady = attackerY - this.host.y;
        const rot = this.host.container.rotation;
        const cosR = Math.cos(-rot);
        const sinR = Math.sin(-rot);
        const localAtkX = adx * cosR - ady * sinR;
        const localAtkY = adx * sinR + ady * cosR;
        const len = Math.sqrt(localAtkX * localAtkX + localAtkY * localAtkY) || 1;
        const nx = localAtkX / len;
        const ny = localAtkY / len;
        const spacing = GOD.componentSpacingMult;
        let bestProj = -Infinity;
        const candidates: InstalledComponent[] = [];
        for (const c of exposed) {
          const proj = c.localX * spacing * nx + c.localY * spacing * ny;
          if (proj > bestProj + 5) {
            bestProj = proj;
            candidates.length = 0;
            candidates.push(c);
          } else if (proj >= bestProj - 5) {
            candidates.push(c);
          }
        }
        target = candidates[Math.floor(Math.random() * candidates.length)];
      } else {
        target = exposed[Math.floor(Math.random() * exposed.length)];
      }

      // Armor shield absorption
      if (target.shieldCurrent > 0) {
        const absorbed = Math.min(amount, target.shieldCurrent);
        target.shieldCurrent -= absorbed;
        amount -= absorbed;
        if (target.shieldCurrent <= 0) {
          EventBus.emit(GameEvents.ARMOR_SHIELD_DEPLETED, { robotId: this.host.id });
        }
      }

      // Remaining damage goes to component HP
      if (amount > 0) {
        target.compCurrentHp -= amount;
        if (target.compCurrentHp <= 0) {
          target.compCurrentHp = 0;
          target.broken = true;
          this.onComponentBroken(target);
        } else {
          this.updateCracks(target);
        }
        this.fx.updateCompSmoke(target);
      }
    }

    // Accumulate damage for popup
    this.pendingPopupDmg += amount;
    if (this.pendingPopupDmg >= 1) {
      this.spawnDamagePopup(this.pendingPopupDmg);
      this.pendingPopupDmg = 0;
    }

    // Always reduce total HP pool
    this.host.currentHp -= amount;
    if (this.host.currentHp <= 0) {
      this.host.currentHp = 0;
      this.host.destroy();
    } else if (amount > 0) {
      EventBus.emit(GameEvents.ROBOT_SURVIVED_HIT, { damage: amount });
    }
    this.host.updateDebugOverlays();
  }

  /** Check if an enemy projectile ricochets off an exposed armor component. */
  checkRicochet(weaponType: EnemyWeaponType, attackerX: number, attackerY: number):
    { normalX: number; normalY: number; impactX: number; impactY: number } | null {
    if (weaponType === EnemyWeaponType.MISSILE_AIR) return null;
    if (weaponType === EnemyWeaponType.FLAMETHROWER) return null;
    const isBeam = weaponType === EnemyWeaponType.LASER;

    const armors = this.host.components.filter(c =>
      c.exposed && !c.broken && c.def.type === ComponentType.ARMOR,
    );
    if (armors.length === 0) return null;

    const adx = attackerX - this.host.x;
    const ady = attackerY - this.host.y;
    const rot = this.host.container.rotation;
    const cosR = Math.cos(-rot);
    const sinR = Math.sin(-rot);
    const localAtkX = adx * cosR - ady * sinR;
    const localAtkY = adx * sinR + ady * cosR;
    const len = Math.sqrt(localAtkX * localAtkX + localAtkY * localAtkY) || 1;
    const nx = localAtkX / len;
    const ny = localAtkY / len;
    const spacing = GOD.componentSpacingMult;

    let bestProj = -Infinity;
    let bestArmor: InstalledComponent | null = null;
    for (const c of armors) {
      const proj = c.localX * spacing * nx + c.localY * spacing * ny;
      if (proj > bestProj) {
        bestProj = proj;
        bestArmor = c;
      }
    }
    if (!bestArmor) return null;

    const chance = isBeam
      ? (bestArmor.def.ricochetBeamChance ?? 0)
      : (bestArmor.def.ricochetChance ?? 0);
    const finalChance = Math.min(1, chance * GOD.armorRicochetMult);
    if (finalChance <= 0 || Math.random() > finalChance) return null;

    EventBus.emit(GameEvents.ARMOR_RICOCHET, { robotId: this.host.id, weaponType });
    const worldLen = Math.sqrt(adx * adx + ady * ady) || 1;
    return {
      normalX: adx / worldLen,
      normalY: ady / worldLen,
      impactX: this.host.x + (adx / worldLen) * (this.host.physicsRadius ?? 20),
      impactY: this.host.y + (ady / worldLen) * (this.host.physicsRadius ?? 20),
    };
  }

  /** Floating damage number above the robot */
  private spawnDamagePopup(dmg: number): void {
    const display = Math.round(dmg);
    if (display <= 0) return;

    const fontSize = popupFontSize(display);
    const strokeW = fontSize >= 20 ? 4 : 3;

    const pDist = GOD.popupDistMult;
    const actualScale = Math.abs(this.host.container.scaleY);

    let topLocalY = 0;
    for (const c of this.host.components) {
      const cy = c.localY * GOD.componentSpacingMult;
      if (cy < topLocalY) topLocalY = cy;
    }
    const topOff = Math.abs(topLocalY) * actualScale;

    const spreadX = (Math.random() - 0.5) * 20 * pDist;
    const startX = this.host.x + spreadX;
    const startY = this.host.y - (topOff + 10) * GOD.popupStartOffsetMult;

    const txt = this.host.scene.add.text(startX, startY, String(display), {
      fontFamily: 'monospace',
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      color: '#ff6644',
      stroke: '#000000',
      strokeThickness: strokeW,
    }).setOrigin(0.5, 1).setDepth(50);

    const driftX = (Math.random() - 0.5) * 15 * pDist;
    const pDur = GOD.popupDurationMult;
    this.host.scene.tweens.add({
      targets: txt,
      y: startY - (20 + Math.random() * 10) * pDist,
      x: startX + driftX,
      alpha: 0,
      duration: (500 + Math.random() * 200) * pDur,
      ease: 'Power2',
      onComplete: () => txt.destroy(),
    });
  }

  /** Called when a component breaks */
  private onComponentBroken(comp: InstalledComponent): void {
    this.removeCracks(comp);
    this.host.invalidateFlyShadow();

    // Break burst VFX
    this.fx.emitBreakBurst(comp);

    // 50/50: eject as debris or keep broken in place
    const eject = Math.random() < 0.5;
    if (eject) {
      comp.ejected = true;
      this.fx.stopCompSmoke(comp);
      this.ejectComponent(comp);
    } else {
      comp.ejected = false;
      if (comp.graphic) {
        this.host.scene.tweens.killTweensOf(comp.graphic);
        comp.graphic.setAlpha(0.25);
        if ((comp.graphic as any).setTint) {
          (comp.graphic as any).setTint(0x444444);
        }
      }
    }

    const label = eject ? 'LOST!' : 'BREAK!';
    const color = eject ? '#ff8800' : '#ff4444';
    SoundManager.play('componentBreak');
    const breakText = this.host.scene.add.text(this.host.x, this.host.y - 16, label, {
      fontSize: '14px', color, fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(200);
    this.host.scene.tweens.add({
      targets: breakText,
      y: breakText.y - 30, alpha: 0, scaleX: 1.3, scaleY: 1.3,
      duration: 700, ease: 'Cubic.easeOut',
      onComplete: () => breakText.destroy(),
    });

    // Check if a flying robot lost all flight components -> crash
    const isFlightComp = comp.def.type === ComponentType.WING || comp.def.type === ComponentType.TURBINE;
    const wasFlyer = this.host.components.some(
      c => c.def.type === ComponentType.WING || c.def.type === ComponentType.TURBINE
    );

    this.host.recalcStats();

    if (isFlightComp && wasFlyer && !this.host.canFly()) {
      this.host.startCrash();
      return;
    }

    // Flight is free — no crash from losing batteries (energy is action-based only)

    if (this.checkStructuralIntegrity()) return;

    if (!this.host.isFallen && !this.host.canFly()) {
      const designedWithMovement = this.host.components.some(
        c => c.def.category === ComponentCategory.MOVEMENT,
      );
      const workingWheels = this.host.components.filter(
        c => !c.broken && c.def.category === ComponentCategory.MOVEMENT,
      );
      const lostAllWheels = designedWithMovement && workingWheels.length === 0;

      if (lostAllWheels || this.host.stability.score < 0.3) {
        const fallDir = Math.sign(this.host.stability.tiltAngle)
          || Math.sign(this.host.stability.comX)
          || (Math.random() < 0.5 ? -1 : 1);
        this.host.topple(fallDir);
      }
    }

    EventBus.emit(GameEvents.COMPONENT_BROKEN, { robot: this.host, component: comp });
  }

  /**
   * Check structural integrity: are all non-broken components still connected?
   * Returns true if the robot was destroyed.
   */
  private checkStructuralIntegrity(): boolean {
    const active = this.host.components.filter(c => !c.broken);
    if (active.length <= 1) return false;

    const THRESHOLD = COMPONENT_SPACING * 1.1;
    const neighbors = new Map<InstalledComponent, InstalledComponent[]>();
    for (const c of active) {
      neighbors.set(c, []);
    }
    const AXIS_THRESHOLD = COMPONENT_SPACING * 0.3;
    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i], b = active[j];
        const dx = Math.abs(a.localX - b.localX);
        const dy = Math.abs(a.localY - b.localY);
        if (dx <= THRESHOLD && dy <= THRESHOLD) {
          if (a.def.type === ComponentType.WHEEL && b.def.type === ComponentType.WHEEL) continue;
          if (a.def.type === ComponentType.WHEEL || b.def.type === ComponentType.WHEEL) {
            const isAxisAligned = dx <= AXIS_THRESHOLD || dy <= AXIS_THRESHOLD;
            if (!isAxisAligned) continue;
          }
          neighbors.get(a)!.push(b);
          neighbors.get(b)!.push(a);
        }
      }
    }

    const visited = new Set<InstalledComponent>();
    const groups: InstalledComponent[][] = [];
    for (const c of active) {
      if (visited.has(c)) continue;
      const group: InstalledComponent[] = [];
      const queue = [c];
      visited.add(c);
      while (queue.length > 0) {
        const cur = queue.shift()!;
        group.push(cur);
        for (const nb of neighbors.get(cur)!) {
          if (!visited.has(nb)) {
            visited.add(nb);
            queue.push(nb);
          }
        }
      }
      groups.push(group);
    }

    if (groups.length <= 1) return false;

    groups.sort((a, b) => {
      const massA = a.reduce((s, c) => s + c.def.mass, 0);
      const massB = b.reduce((s, c) => s + c.def.mass, 0);
      return massB - massA;
    });
    const mainGroup = groups[0];
    const detachedGroups = groups.slice(1);

    for (const group of detachedGroups) {
      for (const comp of group) {
        comp.compCurrentHp = 0;
        comp.broken = true;
        comp.ejected = true;
        this.fx.stopCompSmoke(comp);
        this.removeCracks(comp);
        this.ejectComponent(comp);
      }
    }
    this.host.invalidateFlyShadow();

    SoundManager.play('componentBreak');
    const splitText = this.host.scene.add.text(this.host.x, this.host.y - 24, 'SPLIT!', {
      fontSize: '16px', color: '#ff00ff', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(200);
    this.host.scene.tweens.add({
      targets: splitText,
      y: splitText.y - 35, alpha: 0, scaleX: 1.4, scaleY: 1.4,
      duration: 800, ease: 'Cubic.easeOut',
      onComplete: () => splitText.destroy(),
    });

    const hasWeapon = mainGroup.some(c => c.def.category === ComponentCategory.WEAPON);
    const hasSensor = mainGroup.some(c => c.def.category === ComponentCategory.SENSOR);
    if (mainGroup.length <= 1 || (!hasWeapon && !hasSensor)) {
      this.host.destroy();
      return true;
    }

    this.host.recalcStats();

    if (this.host.canFly()) {
      const hasBattery = mainGroup.some(c => c.def.type === ComponentType.BATTERY);
      if (!hasBattery) {
        this.host.startCrash();
        return false;
      }
    }

    return false;
  }

  /** Eject a broken component from the robot as a physics debris piece */
  ejectComponent(comp: InstalledComponent): void {
    const scene = this.host.scene;
    if (!comp.graphic) return;

    // Remove from wheel/turbine visuals if applicable
    this.fx.removeFromVisualTracking(comp);

    // Kill any running animation and remove from container
    scene.tweens.killTweensOf(comp.graphic);
    this.host.container.remove(comp.graphic);
    comp.graphic.destroy();
    comp.graphic = undefined as any;

    // Compute world position of the component
    const sm = GOD.componentSpacingMult;
    const s = this.host.renderScale;
    const flipX = this.host.flipped ? -1 : 1;
    const rot = this.host.container.rotation;
    const cosR = Math.cos(rot);
    const sinR = Math.sin(rot);
    const lx = (flipX * comp.localX) * sm * s;
    const ly = comp.localY * sm * s;
    const worldX = this.host.x + lx * cosR - ly * sinR;
    const worldY = this.host.y + lx * sinR + ly * cosR;

    // Create a fresh visual for the debris
    const debris = createComponentVisual(scene, comp.def.type, COMPONENT_SPACING, comp.def.color, comp.evoLevel);
    debris.setPosition(worldX, worldY);
    const internalScale = (debris as any).scaleX as number ?? 1;
    const debrisScale = internalScale * s * (0.7 + Math.random() * 0.2);
    debris.setScale(debrisScale);
    (debris as any).setDepth(29);
    debris.setRotation(rot);

    const dx = worldX - this.host.x;
    const dy = worldY - this.host.y;
    const baseDist = Math.sqrt(dx * dx + dy * dy);
    const outAngle = baseDist > 1
      ? Math.atan2(dy, dx) + (Math.random() - 0.5) * 1.0
      : Math.random() * Math.PI * 2;
    const speed = 60 + Math.random() * 80;
    let vx = Math.cos(outAngle) * speed;
    let vy = -Math.abs(Math.sin(outAngle) * speed) - (40 + Math.random() * 60);
    let spin = (Math.random() - 0.5) * 10;
    let age = 0;
    let bounces = 0;
    let curScale = debrisScale;

    const GRAVITY = 350;
    const GROUND_Y = worldY + 40;
    const BOUNCE_DAMPING = 0.3 + Math.random() * 0.2;
    const MAX_LIFETIME = 2.0;
    const hasSmokeTex = scene.textures.exists('fx_smoke');
    let smokeAccum = 0;

    const timer = scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const dt = 16 / 1000;
        age += dt;
        if (age >= MAX_LIFETIME) {
          (debris as any).setAlpha(Math.max(0, (debris as any).alpha - dt * 3));
          if ((debris as any).alpha <= 0) {
            debris.destroy();
            timer.destroy();
          }
          return;
        }

        vy += GRAVITY * dt;
        const nx = (debris as any).x + vx * dt;
        const ny = (debris as any).y + vy * dt;
        debris.setPosition(nx, ny);
        debris.setRotation((debris as any).rotation + spin * dt);

        // Smoke trail
        if (hasSmokeTex) {
          smokeAccum += 16;
          if (smokeAccum >= 100) {
            smokeAccum -= 100;
            const puff = scene.add.image(nx, ny, 'fx_smoke').setDepth(28);
            puff.setScale(0.15 + Math.random() * 0.1);
            puff.setAlpha(0.35);
            puff.setTint(0x666666);
            scene.tweens.add({
              targets: puff,
              y: ny - 10 - Math.random() * 8,
              scaleX: 0.5, scaleY: 0.5,
              alpha: 0,
              duration: 400 + Math.random() * 200,
              onComplete: () => puff.destroy(),
            });
          }
        }

        // Bounce off ground
        if (ny >= GROUND_Y && vy > 0) {
          debris.setPosition(nx, GROUND_Y);
          vy = -vy * BOUNCE_DAMPING;
          vx *= 0.6;
          spin *= 0.5;
          bounces++;
          if (bounces >= 3 || Math.abs(vy) < 15) {
            vy = 0;
            vx *= 0.3;
          }
        }

        // Shrink after first bounce
        if (bounces > 0) {
          curScale *= (1 - dt * 0.5);
          debris.setScale(curScale);
        }
      },
    });
  }

  /** Update crack overlay on a damaged component based on HP ratio */
  updateCracks(comp: InstalledComponent): void {
    if (comp.compMaxHp <= 0) return;
    const ratio = comp.compCurrentHp / comp.compMaxHp;

    let newLevel = 0;
    if (ratio < RobotDamageSystem.CRACK_HEAVY) newLevel = 2;
    else if (ratio < RobotDamageSystem.CRACK_LIGHT) newLevel = 1;

    if (newLevel === comp.crackLevel) return;
    comp.crackLevel = newLevel;

    this.removeCracks(comp);

    if (newLevel === 0) return;

    const gfx = this.host.scene.add.graphics();
    gfx.setPosition(comp.localX, comp.localY);
    if (comp.rotation) gfx.setRotation(comp.rotation);

    const hs = COMPONENT_SPACING / 2;
    const alpha = newLevel === 2 ? 0.9 : 0.6;
    const color = newLevel === 2 ? 0x222222 : 0x444444;
    const lw = newLevel === 2 ? 2 : 1.5;

    gfx.lineStyle(lw, color, alpha);

    gfx.beginPath();
    gfx.moveTo(-hs * 0.8, -hs * 0.6);
    gfx.lineTo(-hs * 0.1, hs * 0.1);
    gfx.lineTo(hs * 0.5, hs * 0.7);
    gfx.strokePath();

    gfx.beginPath();
    gfx.moveTo(hs * 0.6, -hs * 0.8);
    gfx.lineTo(hs * 0.1, -hs * 0.1);
    gfx.lineTo(-hs * 0.4, hs * 0.5);
    gfx.strokePath();

    if (newLevel === 2) {
      gfx.lineStyle(1.5, 0x111111, 0.7);
      gfx.beginPath();
      gfx.moveTo(-hs * 0.3, -hs * 0.9);
      gfx.lineTo(hs * 0.2, 0);
      gfx.lineTo(hs * 0.8, hs * 0.3);
      gfx.strokePath();

      gfx.beginPath();
      gfx.moveTo(hs * 0.3, -hs * 0.4);
      gfx.lineTo(-hs * 0.2, hs * 0.2);
      gfx.lineTo(-hs * 0.7, hs * 0.8);
      gfx.strokePath();
    }

    this.host.container.add(gfx);
    comp.crackOverlay = gfx;
  }

  /** Remove crack overlay from a component */
  removeCracks(comp: InstalledComponent): void {
    if (comp.crackOverlay) {
      comp.crackOverlay.destroy();
      comp.crackOverlay = undefined;
    }
  }

  /** Apply impact damage to components on the side that hits the ground.
   *  @param fallDir +1 = fell right, -1 = fell left
   *  @param topplePivotX pivot X in spacing-scaled coords
   *  @param topplePivotY pivot Y in spacing-scaled coords
   *  @param toppleMaxAngle maximum topple angle (radians)
   */
  applyFallDamage(fallDir: number, topplePivotX: number = 0, topplePivotY: number = 0, toppleMaxAngle: number = Math.PI / 2): void {
    const active = this.host.components.filter(c => !c.broken && c.compMaxHp > 0);
    if (active.length === 0) return;

    const pivotLX = topplePivotX / Math.max(GOD.componentSpacingMult, 0.01);
    const pivotLY = topplePivotY / Math.max(GOD.componentSpacingMult, 0.01);
    const angle = toppleMaxAngle;
    const cosA = Math.cos(angle * (fallDir > 0 ? 1 : -1));
    const sinA = Math.sin(angle * (fallDir > 0 ? 1 : -1));

    const SPACING = COMPONENT_SPACING;
    const groundContacts: { comp: InstalledComponent; depth: number }[] = [];
    for (const c of active) {
      const dx = c.localX - pivotLX;
      const dy = c.localY - pivotLY;
      const rotY = dx * sinA + dy * cosA;
      const depth = rotY + SPACING * 0.5;
      if (depth > -SPACING * 0.3) {
        groundContacts.push({ comp: c, depth: Math.max(0, depth) });
      }
    }

    if (groundContacts.length === 0) return;

    const spanX = Math.max(...active.map(c => c.localX)) - Math.min(...active.map(c => c.localX));
    const spanY = Math.max(...active.map(c => c.localY)) - Math.min(...active.map(c => c.localY));
    const fallHeight = Math.max(spanX, spanY);
    const heightMult = Math.min(3, 1 + (fallHeight / SPACING) * 0.4);

    const angleFraction = angle / (Math.PI / 2);
    const FALL_DAMAGE_MULT = 0.6;
    const impactForce = this.host.totalMass * heightMult * angleFraction * FALL_DAMAGE_MULT;

    const totalDepth = groundContacts.reduce((s, gc) => s + gc.depth + 1, 0);
    for (const gc of groundContacts) {
      const share = (gc.depth + 1) / totalDepth;
      const dmg = impactForce * share;

      gc.comp.compCurrentHp -= dmg;
      if (gc.comp.compCurrentHp <= 0) {
        gc.comp.compCurrentHp = 0;
        gc.comp.broken = true;
        this.onComponentBroken(gc.comp);
      } else {
        this.updateCracks(gc.comp);
      }
      this.fx.updateCompSmoke(gc.comp);
    }
  }
}
